import {Fragment, type ReactNode, useMemo, useState} from 'react';
import {m} from 'framer-motion';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {Price} from '@common/billing/price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Trans} from '@common/i18n/trans';
import {Button} from '@common/ui/buttons/button';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';
import {FormattedCurrency} from '@common/i18n/formatted-currency';
import {useSlidingPill} from '@common/ui/navigation/use-sliding-pill';

/** Primary brand green (Figma: buttons, strokes, yearly prices). */
const G = '#2B7E2D';
/** Figma segmented control ("Individual | Business | Government"). */
const AUDIENCE_ACTIVE = '#3C7D44';
/** Section headings + body (Figma: Monthly, Yearly, feature text). */
const TEXT = '#535353';
/** “Current plan” pill label (Figma inspect). */
const BADGE_LABEL = '#D8562A';
const planRowGapClass =
  'gap-x-16 gap-y-28 sm:gap-x-24 sm:gap-y-32 min-[1400px]:gap-x-24 min-[1400px]:gap-y-36';
/** 1 col → 2 col → 4 col when viewport fits four 334px cards (~1400px+). */
const planGridClass = clsx(
  'mx-auto grid w-full max-w-[1416px] grid-cols-1 justify-items-center sm:grid-cols-2 min-[1400px]:grid-cols-4',
  planRowGapClass,
);

const sectionHeadingClass =
  'text-left font-bold leading-normal text-[#535353] dark:text-[#f3f4f6]';

const planSectionShellClass = 'mx-auto w-full max-w-[1416px]';

/** Top offset for audience tabs below the site header. */
const audienceTabsTopOffsetClass = 'mt-40 md:mt-48';

/** Space below audience tabs before plan sections (Monthly, cards, etc.). */
const planContentBelowTabsClass = 'mt-44 md:mt-52';

export type AudienceSegment = 'individual' | 'business' | 'government';

const BUSINESS_MESSAGE =
  "We're working on plans for businesses both big and small. Thanks for your interest!";

const GOVERNMENT_MESSAGE =
  "We're working on plans for regional and national governments. Thanks for your interest!";

type PlanCardMode = 'monthly' | 'yearly' | 'buy_once';

interface PricingTableProps {
  className?: string;
  audience?: AudienceSegment;
  onAudienceChange?: (audience: AudienceSegment) => void;
}

export function PricingTable({
  className,
  audience: controlledAudience,
  onAudienceChange,
}: PricingTableProps) {
  const query = useProducts();
  const [internalAudience, setInternalAudience] =
    useState<AudienceSegment>('individual');
  const audience = controlledAudience ?? internalAudience;
  const setAudience = onAudienceChange ?? setInternalAudience;

  return (
    <div className={clsx('w-full pb-20', className)}>
      {query.data ? (
        <PlanSections
          plans={query.data.products}
          audience={audience}
          onAudienceChange={setAudience}
        />
      ) : (
        <SkeletonRow />
      )}
    </div>
  );
}

const AUDIENCE_KEYS: AudienceSegment[] = ['individual', 'business', 'government'];

function AudienceTabs({
  audience,
  onChange,
}: {
  audience: AudienceSegment;
  onChange: (v: AudienceSegment) => void;
}) {
  const tabs: {id: AudienceSegment; label: string}[] = [
    {id: 'individual', label: 'Individual'},
    {id: 'business', label: 'Business'},
    {id: 'government', label: 'Government'},
  ];
  const {
    trackRef,
    setItemRef,
    setHoveredKey,
    pillStyle,
    pillTransitionClass,
    isHighlighted,
    highlightedKey,
  } = useSlidingPill(AUDIENCE_KEYS, audience);

  const pillOverlayStyle = pillStyle
    ? {
        left: pillStyle.left - 2,
        top: pillStyle.top - 2,
        width:
          pillStyle.width +
          4 +
          (highlightedKey === 'individual' ? 2 : 0) +
          (highlightedKey === 'government' ? 2 : 0),
        height: pillStyle.height + 4,
        borderColor: AUDIENCE_ACTIVE,
      }
    : null;

  return (
    <div
      ref={trackRef}
      onMouseLeave={() => setHoveredKey(null)}
      className={clsx(
        'relative box-border h-[65px] w-[319px] max-w-[calc(100vw-32px)] flex-none overflow-visible',
        'rounded-[24px] bg-white dark:bg-[#232832]',
      )}
      role="group"
      aria-label="Pricing audience"
    >
      <div
        aria-hidden
        className={clsx(
          'pointer-events-none absolute inset-0 z-0 rounded-[24px]',
          'border-2 border-solid border-[#E9ECF0] dark:border-[#2a2d33]',
        )}
      />
      <div
        aria-hidden
        className={clsx(
          'pointer-events-none absolute z-[1] box-border rounded-[24px]',
          'border-[2px] border-solid bg-white dark:bg-[#232832]',
          pillTransitionClass,
          pillStyle ? 'opacity-100' : 'opacity-0',
        )}
        style={
          pillOverlayStyle
            ? {
                left: pillOverlayStyle.left,
                top: pillOverlayStyle.top,
                width: pillOverlayStyle.width,
                height: pillOverlayStyle.height,
                borderColor: pillOverlayStyle.borderColor,
              }
            : undefined
        }
      />
      <div className="relative z-[2] flex h-[65px] w-full">
        {tabs.map(t => {
          const highlighted = isHighlighted(t.id);
          return (
            <div
              key={t.id}
              ref={setItemRef(t.id)}
              onMouseEnter={() => setHoveredKey(t.id)}
              className="h-[65px] w-[105px] min-w-0 shrink-0"
            >
              <button
                type="button"
                onClick={() => onChange(t.id)}
                aria-pressed={audience === t.id}
                className={clsx(
                  'flex h-full w-full items-center justify-center',
                  'border-0 bg-transparent px-0 text-[16px] font-medium leading-none transition-colors duration-200 ease-out',
                  highlighted
                    ? 'text-[#3C7D44]'
                    : 'text-[#535353] dark:text-[#b3bcc8]',
                )}
              >
                {t.label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionDivider() {
  return (
    <div
      className="mx-auto my-40 h-[2px] w-full max-w-[1209px] bg-[#4C4C4C] dark:bg-[#2a2d33] md:my-52"
      aria-hidden
    />
  );
}

/** Audience tabs alone — sits above all plan content. */
function AudienceTabsHeader({
  audience,
  onAudienceChange,
  className,
}: {
  audience: AudienceSegment;
  onAudienceChange: (v: AudienceSegment) => void;
  className?: string;
}) {
  return (
    <div className={clsx('flex w-full justify-center', className)}>
      <AudienceTabs audience={audience} onChange={onAudienceChange} />
    </div>
  );
}

function findFirstPrice(
  prices: Price[],
  interval: Price['interval'],
  intervalCount: number
): Price | undefined {
  return prices.find(
    p => p.interval === interval && p.interval_count === intervalCount
  );
}

function findMonthlyPrice(prices: Price[]): Price | undefined {
  return (
    findFirstPrice(prices, 'month', 1) ??
    prices.find(p => p.interval === 'month' && p.billing_type !== 'one_time')
  );
}

function findYearlyPrice(prices: Price[]): Price | undefined {
  return (
    findFirstPrice(prices, 'year', 1) ?? prices.find(p => p.interval === 'year')
  );
}

function filterVisible(plans: Product[]): Product[] {
  return plans.filter(p => !p.hidden);
}

function filterByAudience(
  plans: Product[],
  _audience: AudienceSegment
): Product[] {
  return plans;
}

function hasMonthlySection(plan: Product): boolean {
  if (plan.buy_once) return false;
  if (plan.free) return true;
  return !!findMonthlyPrice(plan.prices);
}

function hasBuyOnceSection(plan: Product): boolean {
  return !!plan.buy_once;
}

function yearlySavingsPercent(monthlyPrice: Price, yearlyPrice: Price): number {
  const fullYearAtMonthly = monthlyPrice.amount * 12;
  if (fullYearAtMonthly <= 0) return 0;
  return Math.round((1 - yearlyPrice.amount / fullYearAtMonthly) * 100);
}

function PlanSections({
  plans,
  audience,
  onAudienceChange,
}: {
  plans: Product[];
  audience: AudienceSegment;
  onAudienceChange: (v: AudienceSegment) => void;
}) {
  const visible = useMemo(
    () => filterByAudience(filterVisible(plans), audience),
    [plans, audience]
  );

  const monthlyPlans = useMemo(
    () => visible.filter(hasMonthlySection),
    [visible]
  );
  const buyOncePlans = useMemo(
    () => visible.filter(hasBuyOnceSection),
    [visible]
  );
  const yearlyRowPlans = useMemo(
    () =>
      monthlyPlans.filter(p => !p.free && findYearlyPrice(p.prices) != null),
    [monthlyPlans]
  );
  const showYearlySection = yearlyRowPlans.length > 0;
  const isComingSoonAudience =
    audience === 'business' || audience === 'government';

  const audienceTabsHeader = (
    <AudienceTabsHeader
      audience={audience}
      onAudienceChange={onAudienceChange}
      className={audienceTabsTopOffsetClass}
    />
  );

  return (
    <div className="flex flex-col">
      {audienceTabsHeader}
      {isComingSoonAudience ? (
        <div
          className={clsx(
            'flex min-h-[min(52vh,480px)] flex-col items-center justify-center px-16 pb-40 md:min-h-[min(58vh,560px)] md:pb-56',
            planContentBelowTabsClass,
          )}
        >
          <p className="max-w-[710px] text-center text-[25px] font-bold leading-normal text-[#535353] dark:text-[#b3bcc8]">
            {audience === 'business' ? BUSINESS_MESSAGE : GOVERNMENT_MESSAGE}
          </p>
        </div>
      ) : (
        <section className={clsx(planSectionShellClass, planContentBelowTabsClass)}>
          <h2
            className={clsx('mb-20 md:mb-24 text-[25px] text-center min-[639px]:text-left ml-[10px] min-[768px]:ml-[50px] min-[1146px]:max-[1399px]:ml-[175px]' , sectionHeadingClass)}
          >
            <Trans message="Monthly" />
          </h2>
          <div className={planGridClass}>
            {monthlyPlans.map(plan => (
              <PlanCard key={`${plan.id}-monthly`} plan={plan} mode="monthly" />
            ))}
          </div>
        </section>
      )}

      {!isComingSoonAudience && buyOncePlans.length > 0 ? (
        <Fragment>
          <SectionDivider />
          <section className={planSectionShellClass}>
            <h2
              className={clsx('mb-20 md:mb-24 text-[25px] text-center min-[639px]:text-left ml-[10px] min-[768px]:ml-[50px] min-[1146px]:max-[1399px]:ml-[175px]', sectionHeadingClass)}
            >
              <Trans message="Own your stuff" />
            </h2>
            <div className={planGridClass}>
              {buyOncePlans.map(plan => (
                <PlanCard
                  key={`${plan.id}-buy_once`}
                  plan={plan}
                  mode="buy_once"
                  layout="featured"
                />
              ))}
            </div>
          </section>
        </Fragment>
      ) : null}

      {!isComingSoonAudience && showYearlySection ? (
        <Fragment>
          <SectionDivider />
          <section className={planSectionShellClass}>
            <h2
              className={clsx('mb-20 md:mb-24 text-[25px] text-center min-[639px]:text-left ml-[10px] min-[768px]:ml-[50px] min-[1146px]:max-[1399px]:ml-[175px]', sectionHeadingClass)}
            >
              <Trans message="Yearly" />
            </h2>
            <div className={planGridClass}>
              {yearlyRowPlans.map(plan => (
                <PlanCard key={`${plan.id}-yearly`} plan={plan} mode="yearly" />
              ))}
            </div>
          </section>
        </Fragment>
      ) : null}
    </div>
  );
}

function resolvePriceForMode(
  plan: Product,
  mode: PlanCardMode
): Price | undefined {
  if (mode === 'buy_once') {
    return plan.prices.find(p => p.billing_type === 'one_time');
  }
  if (mode === 'yearly') {
    return findYearlyPrice(plan.prices);
  }
  return findMonthlyPrice(plan.prices);
}

function buildUpgradeRoute(
  plan: Product,
  price: Price | undefined,
  isLoggedIn: boolean,
  isSubscribed: boolean
): string | undefined {
  if (!price) {
    if (!isLoggedIn && plan.free) {
      return '/register?redirectFrom=pricing';
    }
    return undefined;
  }
  if (!isLoggedIn) {
    return '/register?redirectFrom=pricing';
  }
  if (isSubscribed) {
    return `/billing/change-plan/${plan.id}/${price.id}/confirm`;
  }
  if (!plan.free) {
    return `/checkout/${plan.id}/${price.id}`;
  }
  return undefined;
}

type SubscriptionLike = {
  valid?: boolean;
  active?: boolean;
  cancelled?: boolean;
  product_id: number;
  price_id: number;
  price?: Price;
};

function isSubscriptionValid(sub: SubscriptionLike): boolean {
  if (typeof sub.valid === 'boolean') return sub.valid;
  if (typeof sub.active === 'boolean') return sub.active;
  if (typeof sub.cancelled === 'boolean') return !sub.cancelled;
  return false;
}

function subscriptionMatchesPlanCard(
  sub: SubscriptionLike | undefined,
  plan: Product,
  mode: PlanCardMode,
  resolvedPrice: Price | undefined
): boolean {
  if (!sub || !isSubscriptionValid(sub) || plan.free) {
    return false;
  }

  if (sub.product_id !== plan.id) {
    return false;
  }

  if (mode === 'buy_once') {
    return resolvedPrice != null && sub.price_id === resolvedPrice.id;
  }

  // Match only the card for the billing interval the user actually pays for.
  if (resolvedPrice != null && sub.price_id === resolvedPrice.id) {
    return true;
  }

  const subInterval = sub.price?.interval;
  if (mode === 'yearly' && subInterval === 'year') {
    return true;
  }
  if (mode === 'monthly' && subInterval === 'month') {
    return true;
  }

  return false;
}

function isPlanCardCurrent(
  plan: Product,
  mode: PlanCardMode,
  isLoggedIn: boolean,
  validSubs: SubscriptionLike[],
  resolvedPrice: Price | undefined
): boolean {
  if (!isLoggedIn) {
    return false;
  }

  if (plan.free) {
    if (validSubs.length === 0) {
      return true;
    }

    return validSubs.some(
      sub => isSubscriptionValid(sub) && sub.product_id === plan.id
    );
  }

  return validSubs.some(sub =>
    subscriptionMatchesPlanCard(sub, plan, mode, resolvedPrice)
  );
}

interface PlanCardProps {
  plan: Product;
  mode: PlanCardMode;
  layout?: 'default' | 'featured';
}

/** Figma card: 334×371, radius 60, padding ~60 vertical / ~30 horizontal. */
const cardBase =
  'relative box-border flex flex-col rounded-[60px] bg-white dark:bg-[#232832]';

const cardWidthPx = 'w-full max-w-[334px] min-w-0';

/** Yearly rows use taller cards (still 334 wide) — extra price lines + tighter footer gap so lists rarely scroll. */

function PlanCard({plan, mode, layout = 'default'}: PlanCardProps) {
  const {isLoggedIn, isSubscribed, user} = useAuth();
  const monthlyPrice = findMonthlyPrice(plan.prices);
  const yearlyPrice = findYearlyPrice(plan.prices);
  const price = resolvePriceForMode(plan, mode);

  const validSubs = (user?.subscriptions ?? []).filter(isSubscriptionValid);
  const isCurrentPlan = isPlanCardCurrent(
    plan,
    mode,
    isLoggedIn,
    validSubs,
    price
  );
  const showCurrentSubscriptionChrome = isCurrentPlan;

  const upgradeRoute = buildUpgradeRoute(plan, price, isLoggedIn, isSubscribed);
  const continueTo = continueDestination(plan, upgradeRoute, isLoggedIn);

  const showCancelOnly = isCurrentPlan && !plan.free;
  const borderCurrent = showCurrentSubscriptionChrome;
  const cardShell =
    layout === 'featured'
      ? clsx(
          cardBase,
          'box-border min-h-[371px] h-auto',
          cardWidthPx,
          'px-[30px] py-[60px]',
          borderCurrent
            ? 'border-[3px]'
            : 'border-[3px] border-[#E9ECF0] dark:border-[#2a2d33]'
        )
      : clsx(
          cardBase,
          'box-border',
          mode === 'yearly' ? 'h-[436px]' : 'h-[371px]',
          cardWidthPx,
          'px-[30px] py-[60px]',
          borderCurrent
            ? 'border-[3px]'
            : 'border-[3px] border-[#E9ECF0] dark:border-[#2a2d33]'
        );

  const borderStyle = borderCurrent ? {borderColor: G} : undefined;

  const featureVariant = mode === 'yearly' ? 'pricing-yearly' : 'pricing';

  return (
    <m.article {...opacityAnimation} className={cardShell} style={borderStyle}>
      {showCurrentSubscriptionChrome ? (
        <div className="absolute left-1/2 top-0 z-10 flex -translate-x-1/2 -translate-y-1/2 justify-center px-14">
          <span
            className="whitespace-nowrap rounded-[24px] border-[3px] bg-white px-16 py-6 font-bold leading-none dark:bg-[#232832]"
            style={{borderColor: G, color: BADGE_LABEL, fontSize: '20px'}}
          >
            <Trans message="Current plan" />
          </span>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-2">
        <div
          className={clsx(
            'flex min-h-0 w-full flex-1 flex-col items-stretch gap-8 overflow-x-hidden overflow-y-auto text-left [-webkit-overflow-scrolling:touch]'
          )}
        >
          {/* Titles per Figma: avoid duplicating price + product name */}
          {mode === 'buy_once' ? (
            <div
              className="text-center font-bold leading-normal text-black dark:text-[#f3f4f6]"
              style={{fontSize: '20px'}}
            >
              <Trans message={plan.name} />
            </div>
          ) : null}

          <PriceBlock
            plan={plan}
            mode={mode}
            monthlyPrice={monthlyPrice}
            yearlyPrice={yearlyPrice}
            resolvedPrice={price}
            emphasizeCurrentPrice={Boolean(
              isCurrentPlan &&
                !plan.free &&
                (mode === 'monthly' || mode === 'yearly')
            )}
            hideMonthlyPaidTitle={mode === 'monthly' && !plan.free}
          />

          <ProductFeatureList
            product={plan}
            showSectionTitle={false}
            variant={featureVariant}
          />
        </div>

        <div
          className={clsx(
            'mt-auto flex shrink-0 flex-col items-center',
            mode === 'yearly' ? 'pt-8' : 'pt-11'
          )}
        >
          {showCancelOnly ? (
            <Button
              variant="flat"
              color="primary"
              size="md"
              className="flex h-[52px] w-[141px] max-w-[90%] shrink-0 items-center justify-center rounded-[24px] border-2 font-medium text-white md:h-[56px]"
              style={{backgroundColor: G, borderColor: G, fontSize: '12px'}}
              elementType={Link}
              to="/billing/cancel"
            >
              <Trans message="Cancel" />
            </Button>
          ) : plan.free && isCurrentPlan ? null : (
            <ContinueOutlineButton
              to={continueTo}
              disabled={!continueTo}
              mutedLabel={mode === 'yearly'}
              onClick={() => {
                if (isLoggedIn || !price || plan.free) return;
                setInLocalStorage('be.onboarding.selected', {
                  productId: plan.id,
                  priceId: price.id,
                });
              }}
            >
              <Trans message="Continue" />
            </ContinueOutlineButton>
          )}
        </div>
      </div>
    </m.article>
  );
}

function continueDestination(
  plan: Product,
  upgradeRoute: string | undefined,
  isLoggedIn: boolean
): string | undefined {
  if (plan.free && isLoggedIn) {
    return '/drive';
  }
  return upgradeRoute;
}

function ContinueOutlineButton({
  to,
  disabled,
  onClick,
  children,
  mutedLabel = false,
}: {
  to?: string;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  mutedLabel?: boolean;
}) {
  const labelColor = mutedLabel ? '#757575' : G;
  return (
    <Button
      variant="outline"
      color="primary"
      size="md"
      className="flex h-[52px] w-[141px] max-w-[90%] shrink-0 items-center justify-center rounded-[24px] border-2 bg-white font-medium hover:bg-[#FAFAFA] dark:bg-[#232832] dark:hover:bg-[#2f3440] md:h-[56px]"
      style={{
        borderColor: G,
        color: labelColor,
        fontSize: '17px',
        fontWeight: 500,
      }}
      elementType={to && !disabled ? Link : undefined}
      to={to}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/** Strike only the currency amount with a diagonal brand-green bar (yearly tier Figma). */
function StrikethroughMonthlyAmount({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}) {
  return (
    <span className="relative isolate inline-block">
      <span className="relative z-[1] font-bold leading-none text-[#535353] dark:text-[#b3bcc8]">
        <FormattedCurrency value={amount} currency={currency} />
      </span>
      <span
        aria-hidden
        className="pointer-events-none absolute left-[-5%] top-1/2 z-[2] h-[2px] w-[110%] rounded-[1px]"
        style={{
          backgroundColor: G,
          transform: 'translateY(-50%) rotate(-17deg)',
        }}
      />
    </span>
  );
}

function PriceBlock({
  plan,
  mode,
  monthlyPrice,
  yearlyPrice,
  resolvedPrice,
  emphasizeCurrentPrice,
  hideMonthlyPaidTitle,
}: {
  plan: Product;
  mode: PlanCardMode;
  monthlyPrice?: Price;
  yearlyPrice?: Price;
  resolvedPrice?: Price;
  emphasizeCurrentPrice?: boolean;
  hideMonthlyPaidTitle?: boolean;
}) {
  const mutedPrice = emphasizeCurrentPrice ? G : TEXT;

  if (plan.free) {
    return (
      <div className="w-full text-center">
        <div
          className="font-bold leading-none text-black dark:text-[#f3f4f6]"
          style={{fontSize: '25px'}}
        >
          <Trans message="Free" />
        </div>
      </div>
    );
  }

  if (mode === 'yearly' && yearlyPrice && monthlyPrice) {
    const perMonth = yearlyPrice.amount / 12;
    const savePct = yearlySavingsPercent(monthlyPrice, yearlyPrice);
    return (
      <div className="flex w-full min-w-0 flex-col items-center gap-2 text-center">
        <span
          className="font-bold leading-none tracking-tight"
          style={{color: G, fontSize: '25px'}}
        >
          <FormattedCurrency value={perMonth} currency={yearlyPrice.currency} />
        </span>
        <div
          className="flex flex-wrap items-baseline justify-center gap-[2px] font-bold text-[#535353] dark:text-[#b3bcc8]"
          style={{fontSize: '20px'}}
        >
          <StrikethroughMonthlyAmount
            amount={monthlyPrice.amount}
            currency={monthlyPrice.currency}
          />
          <span>/</span>
          <span>
            <Trans message="Month" />
          </span>
        </div>
        <p
          className="mt-2 text-balance px-8 font-bold leading-snug"
          style={{color: G, fontSize: '12px'}}
        >
          <Trans
            message="Billed as :amount per year. Save :save!"
            values={{
              amount: (
                <FormattedCurrency
                  value={yearlyPrice.amount}
                  currency={yearlyPrice.currency}
                />
              ),
              save: `${savePct}%`,
            }}
          />
        </p>
      </div>
    );
  }

  if (mode === 'buy_once' && resolvedPrice) {
    return (
      <div
        className="text-center font-bold"
        style={{color: G, fontSize: '14px'}}
      >
        <FormattedCurrency
          value={resolvedPrice.amount}
          currency={resolvedPrice.currency}
        />
        <span className="ml-8">
          <Trans message="one time payment" />
        </span>
      </div>
    );
  }

  if (monthlyPrice && hideMonthlyPaidTitle) {
    const monthlyPriceClass = emphasizeCurrentPrice
      ? 'font-bold leading-none'
      : 'font-bold leading-none text-[#535353] dark:text-[#b3bcc8]';
    const monthlyPriceStyle = emphasizeCurrentPrice
      ? {color: mutedPrice, fontSize: '20px'}
      : {fontSize: '20px'};

    return (
      <div className="w-full text-center">
        <div className={monthlyPriceClass} style={monthlyPriceStyle}>
          <FormattedCurrency
            value={monthlyPrice.amount}
            currency={monthlyPrice.currency}
          />
          /<Trans message="Month" />
        </div>
      </div>
    );
  }

  return null;
}

function SkeletonRow() {
  return (
    <div className="flex flex-wrap justify-center gap-24">
      <PlanSkeleton />
      <PlanSkeleton />
      <PlanSkeleton />
    </div>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="box-border h-[371px] w-full max-w-[334px] min-w-0 rounded-[60px] border border-[#E9ECF0] bg-white px-[30px] py-[60px] dark:border-[#2a2d33] dark:bg-[#232832]"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
    </m.div>
  );
}
